import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import { useQuery, UseQueryOptions } from 'react-query';
import { Asset, AssetsBySelectionSet } from 'types/AssetWeighted';
import { LookUp } from 'types/LookUp';
import { TagDefinition } from 'types/TagRefDefinition';
import { axiosClient } from './axiosClient';
import { flattenTags } from 'helpers/tags';
import { CancelTokenSource } from 'axios';
import { ReturnComponent } from 'types/ReturnComponent';

const resource = '';

const lookup = {
  GetBenchmarkTypes() {
    return useQuery('benchmark-types', () =>
      axiosClient.get<LookUp[]>(`${resource}/benchmark_types`)
    );
  },
  GetCalculationFrequencies() {
    return useQuery(
      'calculation-frequencies',
      () => axiosClient.get<LookUp[]>(`${resource}/calculation_frequencies`),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetWeightingMethodologies() {
    return useQuery(
      'weighting-methodologies',
      () => axiosClient.get<LookUp[]>(`${resource}/weighting_methodologies`),
      {
        select: ({ data }) =>
          data.map((ele: LookUp) => ({
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetRebalanceFrequencies() {
    return useQuery(
      'rebalance-frequencies',
      () => axiosClient.get<LookUp[]>(`${resource}/rebalance_frequencies`),
      {
        select: ({ data }) =>
          data.map((ele: LookUp) => ({
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetCurrencies() {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['currencies', dataEnvironment?.id],
      () => axiosClient.get<LookUp[]>(`${dataEnvironment?.id}/currencies`),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.code,
            value: ele.id,
          })),
      }
    );
  },
  GetAssetSelectionSets(enabled: boolean = true) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['asset_selection_sets', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${dataEnvironment?.id}/asset_selection_sets`
        ),
      {
        select: ({ data }) =>
          data.map((ele: LookUp) => ({
            label: ele.name,
            value: ele.id,
          })),
        enabled,
      }
    );
  },
  GetTagRefPropertyDefinitions() {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['tag_ref_property_definitions', dataEnvironment?.id],
      () =>
        axiosClient.get<TagDefinition[]>(
          `${dataEnvironment?.id}/tag_ref_property_definitions`
        ),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
            flattenedTags: flattenTags(ele.tags),
          })),
      }
    );
  },
  GetReturnComponents() {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['return-components', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/return_components`
        ),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.resourceId?.toString() || ele.benchmarkId,
            benchmarkType: ele.benchmarkType?.name,
          })),
      }
    );
  },
  GetWeightingComponents(enabled: boolean = true) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['return-components', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/return_components`
        ),
      {
        select: (data) =>
          data.data
            .filter((ele: any) => ele.resourceId)
            .map((ele: any) => ({
              ...ele,
              label: ele.name,
              value: ele.resourceId,
            })),
        enabled,
      }
    );
  },
  // GetReturnComponents should follow this logic (no select mappings)
  GetReturnComponents_(enabled: boolean = true) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['return-components', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/return_components`
        ),
      {
        enabled,
      }
    );
  },
  GetReturnComponentsSearch(
    input: string,
    cancelTokenSource?: CancelTokenSource
  ) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      [
        'search-return-components',
        { input, cancelTokenSource, id: dataEnvironment?.id },
      ],
      () => {
        const source = cancelTokenSource?.token;
        const trimmedInput = input.trim();
        const encodedInput = encodeURIComponent(trimmedInput);
        return axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/return_components?nameFilter=${encodedInput}`,
          {
            cancelToken: source,
          }
        );
      },
      {
        enabled: input.length > 0,
        refetchOnWindowFocus: false,
      }
    );
  },
  GetReturnComponentsQuery(
    dataEnvironment: string,
    input: string,
    abortController: AbortController
  ) {
    const trimmedInput = input.trim();
    const encodedInput = encodeURIComponent(trimmedInput);
    return axiosClient.get<ReturnComponent[]>(
      `${resource}/${dataEnvironment}/return_components?nameFilter=${encodedInput}`,
      {
        signal: abortController.signal,
      }
    );
  },
  // GetWeightingComponents should follow this logic (no select mappings)
  GetWeightingComponents_(enabled: boolean = true) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['return-components', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/return_components`
        ),
      {
        enabled,
      }
    );
  },
  GetHurdleRateCalculationMethods() {
    return useQuery(
      'hurdle-rate-calcuation-methods',
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/hurdle_rate_calculation_methods`
        ),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetCalendars(options?: UseQueryOptions<any, Error>) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['calendars', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/calendars`
        ),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
        ...options,
      }
    );
  },
  GetJobFrequencies() {
    return useQuery(
      ['job-frequencies'],
      () => axiosClient.get<LookUp[]>(`${resource}/job_frequencies`),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetDaysOfWeek() {
    return useQuery(
      ['days-of-week'],
      () => axiosClient.get<LookUp[]>(`${resource}/days_of_the_week`),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetPerformanceVariations() {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['performance-variations', dataEnvironment?.id],
      () =>
        axiosClient.get<LookUp[]>(
          `${resource}/${dataEnvironment?.id}/performance_variations`
        ),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.code,
          })),
      }
    );
  },
  GetAssetsBySelectionSet(selectionSetId: number | undefined) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['asset-by-selection-set', dataEnvironment?.id, selectionSetId],
      () => {
        if (selectionSetId) {
          return axiosClient.get<AssetsBySelectionSet>(
            `${resource}/${dataEnvironment?.id}/asset_selection_sets/${selectionSetId}`
          );
        }
      },
      {
        select: (data) =>
          data?.data?.contents?.map((ele: Asset) => ({
            ...ele,
            label: ele.name,
            value: ele.resourceId,
          })),
      }
    );
  },
  GetNumberOfDaysInYear() {
    return useQuery(
      ['number-of-days-in-year'],
      () => axiosClient.get<LookUp[]>(`${resource}/number_of_days_in_year`),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
  GetValueTypes() {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['value-types', dataEnvironment?.id],
      () => axiosClient.get<LookUp[]>(`${dataEnvironment?.id}/value_types`),
      {
        select: (data) =>
          data.data.map((ele: any) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          })),
      }
    );
  },
};

export default lookup;
